import { FormProvider } from "react-hook-form";

import { Stack, Text } from "@seneca/senekit";

import { MultiLineTextPlaceholder } from "features/common/components/TextPlaceholder";
import { TermsAndConditionsLink } from "features/sign-up/common-routes/components/form";
import { SignUpCardContent } from "features/sign-up/common-routes/components/Layout";
import ErrorMessage from "features/sign-up/components/ErrorMessage";
import {
  SignInEmailInput as SignUpEmailInput,
  SignInWithEmailLinkButton as SignUpWithEmailLinkButton
} from "features/sign-up/sign-in-form/components/SignInForm/components";
import useSignInWithEmailAndOptionalPasswordForm from "features/sign-up/sign-in-form/components/SignInForm/hooks/useSignInWithEmailAndOptionalPasswordForm";

import SignUpFormBottomLink from "../../SignUpFormBottomLink";
import { SignUpFormHeader } from "../SignUpHeaders";

type Props = {
  loading?: boolean;
  prefilledEmail?: string;
  redirectAuthenticatedUserTo?: string;
  onClickUsePasswordInstead: (email: string) => void;
  onSignUpSuccess?: () => void;
  headerMessage?: string;
};

export default function SignUpPasswordlessForm({
  loading,
  prefilledEmail,
  redirectAuthenticatedUserTo,
  headerMessage,
  onClickUsePasswordInstead
}: Props) {
  const { form, errorMessage, onSubmit } =
    useSignInWithEmailAndOptionalPasswordForm({
      prefilledEmail,
      redirectAuthenticatedUserTo
    });

  return (
    <FormProvider {...form}>
      <SignUpCardContent>
        {{
          body: (
            <Stack space={["xlarge", "xxlarge"]}>
              <SignUpFormHeader headerMessage={headerMessage} />

              <MultiLineTextPlaceholder
                loading={loading}
                placeholderHeight="50px"
                placeholderWidth="100%"
                numberOfLines={2}
              >
                <Stack space="large">
                  <SignUpEmailInput
                    email={prefilledEmail}
                    autoFocus={!prefilledEmail}
                    onEnterKeyPress={onSubmit}
                  />
                  <Text size="small" tone="neutral" emphasis="medium">
                    We'll email you a magic link to sign in instantly.
                  </Text>
                </Stack>
              </MultiLineTextPlaceholder>
            </Stack>
          ),
          footer: (
            <Stack space="xxlarge">
              <Stack space="xlarge">
                {errorMessage && (
                  <ErrorMessage>{errorMessage?.message}</ErrorMessage>
                )}
                <SignUpWithEmailLinkButton
                  onSubmit={onSubmit}
                  loading={form.formState.isSubmitting}
                />
                <TermsAndConditionsLink />
              </Stack>

              <SignUpFormBottomLink
                onBottomLinkClick={() =>
                  onClickUsePasswordInstead(form.getValues("email"))
                }
                bottomLinkText="Sign up with password instead"
              />
            </Stack>
          )
        }}
      </SignUpCardContent>
    </FormProvider>
  );
}
